import { Component, ViewEncapsulation, Input, EventEmitter, AfterViewInit, OnDestroy} from '@angular/core';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Loading } from 'src/shared/components/loading/loading';
import { InAppBrowserUtils } from 'src/shared/utils/in-app-browser-utils';
import { Subject, Subscription } from 'rxjs';
import { ModalController } from '@ionic/angular';
import { Router } from '@angular/router';
import { CommonUtils } from 'src/shared/utils/common-utils';
import * as Sentry from '@sentry/angular';

// Shared job and to-do list links open without a login, so their id stays out of Sentry.
export function hideSharedLinkId(path: string | null): string | null {
  return path?.replace(/((?:^|\/)(?:shared-)?(?:job|to-do-list)\/)[^/?#]+/g, '$1:uuid') ?? path;
}

@Component({
    selector: 'success-page',
    templateUrl: './success.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class SuccessPage implements AfterViewInit, OnDestroy {
  pageMessage: string;
  params: SuccessPageParamsModel;
  @Input() modalParams: SuccessPageParamsModel;
  errorMessage: string;
  modalSubscription: Subscription;
  paramsSource: 'modal' | 'navigation' | 'previous';

  constructor(
    private customNavCtrl: CustomNavController,
    private iabUtils: InAppBrowserUtils,
    private modalCtrl: ModalController,
    private router: Router,
    private utils: CommonUtils
  ) {
  }

  ngAfterViewInit() {
    this.modalSubscription = this.modalParams?.dismiss?.subscribe(dissmiss => {
      if (dissmiss) {
        this.dismissModal(this.params.buttonRoute);
      }
    });
  }

  @Loading()
  ionViewDidEnter() {
    const navigationParams = this.modalParams ? null : this.customNavCtrl.getParams();
    this.paramsSource = this.modalParams ? 'modal' : navigationParams ? 'navigation' : 'previous';
    this.params = this.modalParams || navigationParams || this.params;
  }

  goToLearnMore() {
    this.iabUtils.openUrl(this.params.learnMoreLink);
  }

  buttonAction() {
    let error = null;
    try {
      if(this.params?.customAction) {
        this.params.actionSubject?.next();
        return this.params.customAction();
      }
      if (!this.params.externalUrl) {
        if (this.params.buttonRoute) {
          this.params.actionSubject?.next();
          return this.navigateToButtonRoute();
        }
        this.customNavCtrl.back();
      } else {
        this.iabUtils.openUrl(this.params.buttonRoute);
      }
    } catch (err) {
      error = err;
      this.errorMessage = err.error ? err.error.message : err.message;
      this.utils.showError(this.errorMessage);
    } finally {
      if(!error) {
        this.dismissModal(this.params.buttonRoute);
      }
    }
  }

  secondButtonAction() {
    let error = null;
    try {
      return this.params.secondCustomAction();
    } catch (err) {
      error = err;
      this.errorMessage = err.error ? err.error.message : err.message;
      this.utils.showError(this.errorMessage);
    } finally {
      if(!error) {
        this.dismissModal(this.params.buttonRoute);
      }
    }
  }

  navigateToButtonRoute() {
    return this.customNavCtrl.navigateRoot(this.params.buttonRoute, this.params.navigationExtras || {}).catch((error) => {
      this.reportButtonRouteFailure(error);
      throw error;
    });
  }

  // PRO-APP-RV: "Ok" on "Message Sent" navigates to the route 'undefined' and nothing happens. The NG04002 itself
  // carries no app frame, so report what the button was given and where it came from. Remove once the source is found.
  reportButtonRouteFailure(error) {
    let storedReturnRoute: string | null = null;
    try {
      storedReturnRoute = localStorage.getItem('pathAfterReportIssue');
    } catch {}
    Sentry.captureMessage('Success page button route failed', {
      level: 'warning',
      tags: { success_header: this.params?.header, params_source: this.paramsSource },
      extra: {
        error: String(error?.message || error),
        buttonRoute: JSON.stringify(typeof this.params?.buttonRoute === 'string' ? hideSharedLinkId(this.params.buttonRoute) : this.params?.buttonRoute) ?? 'undefined',
        buttonRouteType: typeof this.params?.buttonRoute,
        storedReturnRoute: JSON.stringify(hideSharedLinkId(storedReturnRoute)),
        routerUrl: hideSharedLinkId(this.router.url),
        historyStateKeys: Object.keys(history.state || {}),
        paramsKeys: Object.keys(this.params || {}),
      },
    });
  }

  ngOnDestroy() {
    this.modalSubscription?.unsubscribe();
  }

  dismissModal(targetRoute = null) {
    try {
      this.modalCtrl.dismiss();
    } catch (e) {}
    if (targetRoute && this.router.url.includes(targetRoute)) {
      window.location.reload();
    }
  }
}

export interface SuccessPageParamsModel {
  header: string;
  externalUrl?: boolean;
  body: string;
  buttonText: string;
  learnMoreLink?: string;
  buttonRoute?: string;
  navigationExtras?: {[key: string]: string}
  customAction?: (data?) => Promise<any> | void;
  secondButtonText?: string;
  secondCustomAction?: (data?) => Promise<any>;
  dismiss?: EventEmitter<boolean>;
  actionSubject?: Subject<void>;
}
